import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTheme } from "styled-components";
import { useDispatch } from "react-redux";

import {
  BillingIcon,
  ProfileIcon,
  ArrowLeftIcon,
  Security2Icon,
  SupportIcon,
  // PartnershipsIcon,
} from "assets/icons";
import {
  ACCOUNT_OVERVIEW_V2_PATH,
  ACCOUNT_PLAN_BILLING_V2_PATH,
  ACCOUNT_SECURITY_V2_PATH,
} from "features/Account/routes";

import { isChildRoute } from "utils";
import { useNavigation } from "utils/navigate";
import { Button } from "components/Button";
import LocationPath from "hooks/locationPath";
import { setCollapsedSidebar } from "Layout/slice";
import { AppMenuContainer } from "./Sidebar.styles";
import { closeIcon, hamburgerIcon } from "assets/icons";
import { Tooltip } from "components/Tooltip";
import { HELP_CENTER_V2_PATH } from "features/HelpCenter/routes";
// import { ACCOUNT_PARTNERSHIPS_V2_PATH } from "features/Account/routes";
// import { PartnershipsIcon } from "assets/icons";

export const ProfileMenu = ({ active = "" }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const collapsed = false;

  const [showBackButton, setShowBackButton] = useState(false);

  const {
    navigateToV2AccountOverview,
    navigateToV2Receipts,
    navigateBackToParent,
    navigateToV2AccountSecurity,
    navigateToV2HelpCenter,
    // navigateToV2Partnerships,
  } = useNavigation();

  const handleNavigateToParent = () => {
    navigateBackToParent();
  };

  const handleChangePath = useCallback((pathname) => {
    if (isChildRoute(pathname)) {
      /**
       * Dont show back button in sidebar: MVP2-1865
       * Not completely removing whole logic so if we need to revert this
       */
      // setShowBackButton(true);
    } else {
      setShowBackButton(false);
    }
  }, []);

  const toggleCollapsed = () => {
    dispatch(setCollapsedSidebar(false));
  };

  const BackIcon = () => {
    return (
      <Button
        size="small"
        className="move-back-btn"
        onClick={handleNavigateToParent}
      >
        <ArrowLeftIcon fill="#fff" />
      </Button>
    );
  };

  return (
    <AppMenuContainer>
      <LocationPath changedPath={handleChangePath} />
      <ul>
        <li className={"menu-item active close-menu"} onClick={toggleCollapsed}>
          <div className="inner-item" tabIndex={0} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {collapsed && (
                  <img
                    alt="toggle"
                    src={hamburgerIcon}
                    className="hamburger-icon"
                  />
                )}
                {!collapsed && (
                  <img
                    alt="toggle"
                    src={closeIcon}
                    className="hamburger-icon"
                  />
                )}
              </span>
            </span>
            <span className="item-content">Close</span>
          </div>
        </li>

        {showBackButton && (
          <li className="menu-item active">
            <div className="inner-item" tabIndex={1} role="button">
              {collapsed && (
                <span className="icon-wrapper">
                  <span className="icon">
                    <BackIcon fill={theme.aside.icon.primary} />
                  </span>
                </span>
              )}
              {!collapsed && (
                <span className="item-content mx-0">
                  <Button
                    size="small"
                    className="move-back-btn"
                    width="100%"
                    onClick={handleNavigateToParent}
                  >
                    <ArrowLeftIcon fill="#fff" /> Back
                  </Button>
                </span>
              )}
            </div>
          </li>
        )}

        <li
          className={classNames("menu-item wt-nav-portfolio", {
            active: active.startsWith(ACCOUNT_OVERVIEW_V2_PATH),
          })}
          onClick={navigateToV2AccountOverview}
        >
          <div className="inner-item" tabIndex={2} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {!collapsed ? (
                  <ProfileIcon
                    stroke={
                      active.startsWith(ACCOUNT_OVERVIEW_V2_PATH)
                        ? theme.aside.icon.primary
                        : theme.aside.icon.secondary
                    }
                  />
                ) : (
                  <Tooltip value="Dashboard">
                    <ProfileIcon
                      stroke={
                        active.startsWith(ACCOUNT_OVERVIEW_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  </Tooltip>
                )}
              </span>
            </span>
            {!collapsed && (
              <span className="item-content mt-1">My profile</span>
            )}
          </div>
        </li>

        <li
          className={classNames("menu-item wt-nav-marketplace", {
            active: active.startsWith(ACCOUNT_PLAN_BILLING_V2_PATH),
          })}
          onClick={navigateToV2Receipts}
        >
          <div className="inner-item" tabIndex={3} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                <div className="marketplace-icon">
                  {!collapsed ? (
                    <BillingIcon
                      fill={
                        active.startsWith(ACCOUNT_PLAN_BILLING_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  ) : (
                    <Tooltip value="Billing">
                      <BillingIcon
                        fill={
                          active.startsWith(ACCOUNT_PLAN_BILLING_V2_PATH)
                            ? theme.aside.icon.primary
                            : theme.aside.icon.secondary
                        }
                      />
                    </Tooltip>
                  )}
                </div>
              </span>
            </span>
            {!collapsed && <span className="item-content mt-1">Billing</span>}
          </div>
        </li>

        <li
          className={classNames("menu-item wt-nav-own-strategy", {
            active: active.startsWith(ACCOUNT_SECURITY_V2_PATH),
          })}
          onClick={navigateToV2AccountSecurity}
        >
          <div className="inner-item" tabIndex={4} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {!collapsed ? (
                  <Security2Icon
                    stroke={
                      active.startsWith(ACCOUNT_SECURITY_V2_PATH)
                        ? theme.aside.icon.primary
                        : theme.aside.icon.secondary
                    }
                  />
                ) : (
                  <Tooltip value="Create Strategy">
                    <Security2Icon
                      stroke={
                        active.startsWith(ACCOUNT_SECURITY_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  </Tooltip>
                )}
              </span>
            </span>
            {!collapsed && <span className="item-content mt-1">Security</span>}
          </div>
        </li>

        {/* <li
          className={classNames("menu-item", {
            active: active.startsWith(ACCOUNT_INVITE_V2_PATH),
          })}
          onClick={navigateToV2Invite}
        >
          <div className="inner-item" tabIndex={5} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {!collapsed ? (
                  <InviteIcon
                    fill={
                      active.startsWith(ACCOUNT_INVITE_V2_PATH)
                        ? theme.aside.icon.primary
                        : theme.aside.icon.secondary
                    }
                  />
                ) : (
                  <Tooltip tooltipValue="Markets">
                    <InviteIcon
                      fill={
                        active.startsWith(ACCOUNT_INVITE_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  </Tooltip>
                )}
              </span>
            </span>
            {!collapsed && <span className="item-content">Invite & Earn</span>}
          </div>
        </li> */}

        <li
          className={classNames("menu-item", {
            active: active.startsWith(HELP_CENTER_V2_PATH),
          })}
          onClick={() => navigateToV2HelpCenter()}
        >
          <div className="inner-item" tabIndex={5} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {!collapsed ? (
                  <SupportIcon
                    fill={
                      active.startsWith(HELP_CENTER_V2_PATH)
                        ? theme.aside.icon.primary
                        : theme.aside.icon.secondary
                    }
                  />
                ) : (
                  <Tooltip tooltipValue="Help center">
                    <SupportIcon
                      fill={
                        active.startsWith(HELP_CENTER_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  </Tooltip>
                )}
              </span>
            </span>
            {!collapsed && <span className="item-content">Support & help</span>}
          </div>
        </li>

        {/* <li
          className={classNames("menu-item", {
            active: active.startsWith(ACCOUNT_PARTNERSHIPS_V2_PATH),
          })}
          onClick={navigateToV2Partnerships}
        >
          <div className="inner-item" tabIndex={6} role="button">
            <span className="icon-wrapper">
              <span className="icon">
                {!collapsed ? (
                  <PartnershipsIcon
                    fill={
                      active.startsWith(ACCOUNT_PARTNERSHIPS_V2_PATH)
                        ? theme.aside.icon.primary
                        : theme.aside.icon.secondary
                    }
                  />
                ) : (
                  <Tooltip tooltipValue="Partnerships">
                    <PartnershipsIcon
                      fill={
                        active.startsWith(ACCOUNT_PARTNERSHIPS_V2_PATH)
                          ? theme.aside.icon.primary
                          : theme.aside.icon.secondary
                      }
                    />
                  </Tooltip>
                )}
              </span>
            </span>
            {!collapsed && <span className="item-content">Partnerships</span>}
          </div>
        </li> */}
      </ul>
    </AppMenuContainer>
  );
};

ProfileMenu.propTypes = {
  active: PropTypes.string,
};
